import * as React from "react";

import { ActionsItem } from "@common/components/actions";
import { trDts } from "@modules/datasheets/utils/i18n";
import { getTestAttributes } from "@common/utils/tests";
import { DATASHEETS_MODULE_NAME } from "@modules/datasheets/constants/common";
import { useMessage } from "@common/hooks/message";
import { InfoMessage } from "@common/components/info-message";

import { DatasheetTabNameManagementMode } from "./components/tab-name-management/tab-name-management.types";
import { DatasheetEditorHeaderProps } from "./header.types";
import { GridSettings } from "./components/grid-settings";
import { DatasheetElementAlignment, DatasheetElementSizeAlignment } from "../../types/alignment";

export const useController = (props: DatasheetEditorHeaderProps) => {
  const {
    datasheet,
    disabledState,
    onAddText,
    onSave,
    onSaveAs,
    onPrint,
    onUndo,
    onRedo,
    onDatasheetElementCopy,
    onDatasheetElementPaste,
    onSelectAll,
    onLoadDatasheet,
    onNewDatasheet,
    onGridSettingsChange,
    onResetTabOrder,
    onDatasheetElementAlignmentChange,
    onDatasheetElementSizeAlignmentChange,
    onTabAdd,
    onTabRename,
    onTabDelete,
    onTabCopy,
    onTabPaste
  } = props;

  const [tabManagementMode, setTabManagementMode] = React.useState<DatasheetTabNameManagementMode | undefined>();

  const { closeMessage, showConfirmMessage, showErrorMessage, messageProps } = useMessage();

  const closeTabManagement = React.useCallback(() => {
    setTabManagementMode(undefined);
  }, []);

  const handleTabSubmit = ({ tabName }: { tabName: string }) => {
    if (tabManagementMode === DatasheetTabNameManagementMode.add) {
      onTabAdd(tabName);
    }

    if (tabManagementMode === DatasheetTabNameManagementMode.rename) {
      onTabRename(tabName);
    }

    if (tabManagementMode === DatasheetTabNameManagementMode.paste) {
      onTabPaste(tabName);
    }

    closeTabManagement();
  };

  const handleDatasheetElementPaste = React.useCallback(() => {
    const result = onDatasheetElementPaste();

    if (result.hasDuplicatedDatasheetElements) {
      showErrorMessage(trDts('fieldExistsInTab'));
    }
  }, [onDatasheetElementPaste, showErrorMessage]);

  const actions: Array<ActionsItem> = [
    {
      iconSrc: "gif/add_tab.gif",
      label: trDts("Tabs"),
      disabled: disabledState.tabs,
      dropdownProps: {
        options: [
          {
            label: trDts("Add Tab"),
            onClick: () => {
              setTabManagementMode(DatasheetTabNameManagementMode.add);
            },
          },
          {
            label: trDts("Rename Tab"),
            onClick: () => {
              setTabManagementMode(DatasheetTabNameManagementMode.rename);
            },
          },
          {
            label: trDts("Copy Tab"),
            onClick: onTabCopy,
          },
          {
            disabled: disabledState.tabPaste,
            label: trDts("Paste Tab"),
            onClick: () => {
              setTabManagementMode(DatasheetTabNameManagementMode.paste);
            },
          },
          {
            label: trDts("Delete Tab"),
            onClick: () => {
              showConfirmMessage({
                header: trDts("Delete Tab"),
                content: <InfoMessage>{trDts("Are you sure you want to delete this tab?")}</InfoMessage>,
                confirmText: trDts("Delete"),
                cancelText: trDts("Cancel"),
                onCustomOk: () => {
                  onTabDelete();
                },
              });
            },
          },
        ],
      },
    },
    {
      iconSrc: "gif/add_text.gif",
      label: trDts("Add Text"),
      disabled: disabledState.addText,
      onClick: onAddText,
    },
    {
      iconSrc: "gif/save.gif",
      label: trDts("Save"),
      onClick: onSave,
    },
    {
      iconSrc: "gif/save.gif",
      label: trDts("Save As"),
      onClick: onSaveAs,
    },
    {
      iconSrc: "gif/print.gif",
      label: trDts("Print"),
      disabled: disabledState.print,
      onClick: onPrint,
    },
    {
      iconSrc: "png/layout.png",
      label: trDts("Grid Opt"),
      dropdownProps: {
        classes: {
          menuContainer: "DatasheetsEditor__header__grid-settings-menu-container",
        },
        useDefaultPosition: true,
        customDropdown: (
          <GridSettings 
            onResetTabOrder={onResetTabOrder}
            settings={datasheet.gridSettings} 
            onChange={onGridSettingsChange}
          />
        )
      },
    },
    {
      iconSrc: "png/undo.png",
      label: trDts("Undo"),
      disabled: disabledState.undo,
      onClick: onUndo,
    },
    {
      iconSrc: "png/redo.png",
      label: trDts("Redo"),
      disabled: disabledState.redo,
      onClick: onRedo,
    },
    {
      iconSrc: "png/copy-files.png",
      label: trDts("Copy"),
      disabled: disabledState.datasheetElementCopy,
      onClick: onDatasheetElementCopy,
    },
    {
      iconSrc: "png/paste.png",
      label: trDts("Paste"),
      disabled: disabledState.datasheetElementPaste,
      onClick: handleDatasheetElementPaste,
    },
    {
      iconSrc: "png/select.png",
      label: trDts("Select All"),
      disabled: disabledState.selectAll,
      onClick: onSelectAll,
    },
    {
      iconSrc: "gif/align.gif",
      label: trDts("Alignment"),
      disabled: disabledState.alignment,
      dropdownProps: {
        options: [
          {
            label: trDts("Align Left"),
            onClick: () => onDatasheetElementAlignmentChange(DatasheetElementAlignment.left),
          },
          {
            label: trDts("Align Right"),
            onClick: () => onDatasheetElementAlignmentChange(DatasheetElementAlignment.right),
          },
          {
            label: trDts("Align Top"),
            onClick: () => onDatasheetElementAlignmentChange(DatasheetElementAlignment.top),
          },
          {
            label: trDts("Align Bottom"),
            onClick: () => onDatasheetElementAlignmentChange(DatasheetElementAlignment.bottom),
          },
          {
            label: trDts("Size Horz"),
            onClick: () => onDatasheetElementSizeAlignmentChange(DatasheetElementSizeAlignment.horizontal),
          },
          {
            label: trDts("Size Vert"),
            onClick: () => onDatasheetElementSizeAlignmentChange(DatasheetElementSizeAlignment.vertical),
          },
        ]
      },   
    },
  ].map(({ label, ...restData }) => ({
    ...restData,
    id: getTestAttributes(DATASHEETS_MODULE_NAME.editor, "header-action", "btn", label.replace(" ", "-").toLowerCase())
      .id,
    label: label.toString(),
  }));

  const buttons = [
    {
      label: trDts("Load Datasheet"),
      onClick: onLoadDatasheet,
    },
    {
      label: trDts("New Datasheet"),
      onClick: onNewDatasheet,
    },
  ];

  return {
    buttons,
    actions,
    datasheet,
    messageProps,
    tabManagementMode,
    closeMessage,
    handleTabSubmit,
    closeTabManagement,
  };
};
